<template>
  <div class="create-new-entry-point">
    <!-- Guide Drawer Overlay -->
    <transition name="cep-overlay-fade">
      <div
        v-if="showGuide"
        class="cep-drawer-overlay"
        @click.self="showGuide = false"
      ></div>
    </transition>

    <!-- Guide Drawer -->
    <transition name="cep-drawer-slide">
      <div v-if="showGuide" class="cep-guide-drawer">
        <div class="cep-drawer-header">
          <div>
            <h2 class="cep-drawer-title">Entry point guide</h2>
            <p class="cep-drawer-subtitle">
              Read-only reference — does not affect the chat
            </p>
          </div>
          <button class="cep-drawer-close" @click="showGuide = false">
            &times;
          </button>
        </div>

        <div class="cep-drawer-body">
          <!-- One-shot example -->
          <div class="cep-guide-example">
            <div class="cep-guide-example-label">ONE-SHOT EXAMPLE</div>
            <p class="cep-guide-example-text">
              "WhatsApp + Messenger, MyDawa Kenya, contraceptive campaign
              targeting women 18–25. CTA: Get confidential guidance now. Land
              users at the contraceptive selection stage."
            </p>
          </div>

          <!-- Mandatory section -->
          <div class="cep-guide-section-title">
            MANDATORY — AI ALWAYS COLLECTS
          </div>

          <div class="cep-guide-item">
            <span class="cep-guide-dot"></span>
            <div>
              <div class="cep-guide-item-header">
                Ad creative or context
                <span class="cep-badge cep-badge-first">First</span>
              </div>
              <p class="cep-guide-item-desc">
                Upload image or describe context. Used to infer programme,
                journey, and generate messaging.
              </p>
            </div>
          </div>

          <div class="cep-guide-item">
            <span class="cep-guide-dot"></span>
            <div>
              <div class="cep-guide-item-header">
                Customer / Client
                <span class="cep-badge cep-badge-required">Required</span>
              </div>
              <p class="cep-guide-item-desc">
                AI asks early in chat. Determines which journey defaults and
                journeys are loaded.
              </p>
            </div>
          </div>

          <div class="cep-guide-item">
            <span class="cep-guide-dot"></span>
            <div>
              <div class="cep-guide-item-header">
                Channel(s)
                <span class="cep-badge cep-badge-required">Required</span>
              </div>
              <p class="cep-guide-item-desc">
                WhatsApp, Messenger, or LINE. Inferred from context where
                possible.
              </p>
            </div>
          </div>

          <div class="cep-guide-item">
            <span class="cep-guide-dot"></span>
            <div>
              <div class="cep-guide-item-header">
                Journey + Stage
                <span class="cep-badge cep-badge-required">Required</span>
              </div>
              <p class="cep-guide-item-desc">
                Describe naturally. AI resolves via Country › Customer folder
                structure.
              </p>
            </div>
          </div>

          <div class="cep-guide-item">
            <span class="cep-guide-dot"></span>
            <div>
              <div class="cep-guide-item-header">
                Phrase
                <span class="cep-badge cep-badge-required">Required</span>
              </div>
              <p class="cep-guide-item-desc">
                AI suggests from ad context. Min 1. WA: 1,024 / MS: 640 / LINE:
                2,000 chars.
              </p>
            </div>
          </div>

          <div class="cep-guide-item">
            <span class="cep-guide-dot"></span>
            <div>
              <div class="cep-guide-item-header">
                Welcome message (all languages)
                <span class="cep-badge cep-badge-required">Required</span>
              </div>
              <p class="cep-guide-item-desc">
                AI generates per active language. Must confirm before output
                generation.
              </p>
            </div>
          </div>

          <!-- Auto-applied section -->
          <div class="cep-guide-section-title cep-section-blue">
            AUTO-APPLIED FROM JOURNEY DEFAULTS
          </div>

          <div class="cep-guide-item cep-item-blue">
            <span class="cep-guide-dot cep-dot-blue"></span>
            <div>
              <div class="cep-guide-item-header">
                Onboarding mode, NLP question, Auto-accept T&amp;C, Club Optin,
                Default gender, Referral priority, Ownership/Physical/Medical
                filters
                <span class="cep-badge cep-badge-silent">Silent</span>
              </div>
              <p class="cep-guide-item-desc">
                Retrieved from Journey defaults and applied without asking.
                Shown in confirmation summary. Override in chat.
              </p>
            </div>
          </div>

          <!-- Campaign metadata section -->
          <div class="cep-guide-section-title cep-section-orange">
            CAMPAIGN METADATA — ASKED ONCE AT THE END
          </div>

          <div class="cep-guide-item cep-item-orange">
            <span class="cep-guide-dot cep-dot-orange"></span>
            <div>
              <div class="cep-guide-item-header">
                Keyword Use, Keyword Topic, Campaign, Campaign Code, UTM Source,
                UTM Medium
                <span class="cep-badge cep-badge-optional">Optional</span>
              </div>
              <p class="cep-guide-item-desc">
                AI asks once at the end: "Do you want to add campaign metadata?"
                User can fill in or skip entirely.
              </p>
            </div>
          </div>

          <!-- Optional — in output panel -->
          <div class="cep-guide-section-title cep-section-gray">
            OPTIONAL — IN OUTPUT PANEL
          </div>

          <div class="cep-guide-item cep-item-gray">
            <span class="cep-guide-dot cep-dot-gray"></span>
            <div>
              <div class="cep-guide-item-header">
                Custom short URL
                <span class="cep-badge cep-badge-optional-gray">Optional</span>
              </div>
              <p class="cep-guide-item-desc">
                Override auto-generated slug. Warning shown: changing this
                updates the QR code.
              </p>
            </div>
          </div>

          <div class="cep-guide-item cep-item-gray">
            <span class="cep-guide-dot cep-dot-gray"></span>
            <div>
              <div class="cep-guide-item-header">
                QR code logo
                <span class="cep-badge cep-badge-optional-gray">Optional</span>
              </div>
              <p class="cep-guide-item-desc">Toggle Nivi logo. Default off.</p>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <!-- New Chat Confirmation Dialog -->
    <transition name="cep-overlay-fade">
      <div v-if="showNewChatDialog" class="cep-dialog-overlay" @click.self="showNewChatDialog = false">
        <div class="cep-dialog">
          <h4 class="cep-dialog-title">Start a new chat?</h4>
          <p class="cep-dialog-body">Your current conversation will be saved and a fresh chat will begin.</p>
          <div class="cep-dialog-actions">
            <button class="cep-dialog-btn cep-dialog-btn-cancel" @click="showNewChatDialog = false">No</button>
            <button class="cep-dialog-btn cep-dialog-btn-confirm" @click="confirmNewChat">Yes</button>
          </div>
        </div>
      </div>
    </transition>

    <!-- Main body: left column (topbar + chat) + right column (output) -->
    <div class="cep-body">
      <div class="cep-left-column">
        <!-- Topbar -->
        <div class="cep-topbar">
          <div class="cep-topbar-info">
            <h4 class="cep-topbar-title">Contextual Entry Console</h4>
            <p class="cep-topbar-subtitle">
              Share your ad creative or describe the context — AI configures your
              entry point
            </p>
          </div>
          <div class="cep-topbar-actions">
            <button class="cep-new-chat-btn" @click="showNewChatDialog = true">
              + New Chat
            </button>
            <button class="cep-guide-btn cep-export-btn" @click="printChat">
              <i class="fa fa-download"></i> Export
            </button>
            <button class="cep-guide-btn" @click="showGuide = true">
              <i class="fa fa-info-circle"></i> Guide
            </button>
          </div>
        </div>
        <div v-if="!chatReady" class="cep-chat-loading">
          <div class="cep-chat-loading-inner">
            <div class="cep-chat-spinner" />
            <span class="cep-chat-loading-text">Setting up your chat…</span>
          </div>
        </div>
        <chat-panel v-if="showChat" v-show="chatReady" ref="chatPanel" @ready="chatReady = true" @entry-point-created="onEntryPointCreated" />
      </div>
      <div v-if="loading" class="cep-output cep-output-loading">
        <i class="fa fa-spinner fa-spin"></i>
        <span>Loading entry point…</span>
      </div>
      <output-sidebar v-else class="cep-output" :output="entryPointOutput" @save="onOutputSave" @output_panel_edit="p => onOutputEvent('output_panel_edit', p)" @output_copied="p => onOutputEvent('output_copied', p)" @output_downloaded="p => onOutputEvent('output_downloaded', p)" @short_url_change_warned="p => onOutputEvent('short_url_change_warned', p)" @short_url_change_confirmed="p => onOutputEvent('short_url_change_confirmed', p)" @short_url_change_cancelled="p => onOutputEvent('short_url_change_cancelled', p)" />
    </div>
  </div>
</template>

<script>
import ChatPanel from './chat-panel/ChatPanel.vue'
import OutputSidebar from './output-sidebar/OutputSidebar.vue'
import api from '../../../../utils/api'
import trackEvent from '../../../../utils/trackEvent'

export default {
  name: 'CreateNewEntryPoint',
  components: {
    ChatPanel,
    OutputSidebar
  },
  data () {
    return {
      showGuide: false,
      showNewChatDialog: false,
      showChat: true,
      chatReady: false,
      loading: false,
      entryPointOutput: null
    }
  },
  watch: {
    showGuide (val) {
      if (val) {
        const chatId = this.$refs.chatPanel && this.$refs.chatPanel.chatId
        trackEvent({ event_name: 'blueprint_opened', ai_console_chat_id: chatId })
      }
    }
  },
  methods: {
    async onEntryPointCreated (payload) {
      const entryPointId = payload && payload.entry_point_id
      const keywordId = payload && payload.keyword_id
      if (!entryPointId || !keywordId) return

      this.loading = true
      this.entryPointOutput = null
      try {
        const [kwRes, phraseRes] = await Promise.all([
          api.buildRequest('getKeywordDetails', 'POST', {
            selector: { id: keywordId },
            sort: { createdAt: -1 },
            limit: 20,
            skip: 0
          }),
          api.buildRequest('getPhraseList', 'POST', {
            selector: { keyword_id: keywordId },
            sort: { createdAt: -1 }
          })
        ])
        const kd = kwRes.data.payload.keywordDetails
        const keyword = { ...kd.data, id: kd.id }
        const phrase = phraseRes.data.payload.phrases || []
        this.entryPointOutput = { keyword, phrase, entryPointId }
      } catch (err) {
        console.error('[EntryPoint] failed to load entry point output', err)
      } finally {
        this.loading = false
      }
    },
    printChat () {
      const messages = this.$refs.chatPanel.messages
      const rows = messages.map(m => {
        const isUser = m.role === 'user'
        const time = m.timestamp ? new Date(m.timestamp).toLocaleTimeString() : ''
        const bubbleStyle = isUser
          ? 'margin-left:auto;background:#e6f7f7;border-radius:14px 14px 2px 14px;'
          : 'margin-right:auto;background:#f5f5f5;border-radius:14px 14px 14px 2px;'
        const label = isUser ? 'You' : 'NiVi AI'
        let body = ''
        if (m.text) {
          body = `<p style="margin:0;white-space:pre-wrap;">${m.text}</p>`
        } else if (m.content) {
          const chip = s => `<span style="display:inline-block;border:1px solid #ccc;border-radius:20px;padding:2px 10px;margin:2px;font-size:12px;">${s}</span>`
          body = m.content.map(el => {
            if (el.type === 'text') return `<p style="margin:0 0 6px;white-space:pre-wrap;">${el.value || ''}</p>`
            if (el.type === 'config') {
              return `<div style="font-size:12px;margin:4px 0;">${(el.items || []).map(b => `<strong>${b.label}:</strong> ${b.value}`).join('<br>')}</div>`
            }
            if (el.type === 'badge-row') {
              return `<div style="margin:4px 0;">${(el.items || []).map(b => chip(b.label || b)).join(' ')}</div>`
            }
            if (el.type === 'chips') {
              return `<div style="margin:4px 0;">${(el.items || []).map(c => chip(typeof c === 'string' ? c : c.label || c.value || '')).join(' ')}</div>`
            }
            if (el.type === 'multi_chips') {
              return `<div style="margin:4px 0;">${(el.items || []).map(c => chip(typeof c === 'string' ? c : c.label || c.value || '')).join(' ')}</div>`
            }
            if (el.type === 'cards') {
              return (el.items || []).map(c => `<div style="border:1px solid #ddd;border-radius:6px;padding:8px;margin:4px 0;font-size:12px;"><strong>${c.title}</strong>${c.subtitle ? '<br><small>' + c.subtitle + '</small>' : ''}</div>`).join('')
            }
            if (el.type === 'buttons') {
              return `<div style="margin:4px 0;">${(el.items || []).map(b => `<span style="display:inline-block;border:1px solid #2ab6b6;border-radius:20px;padding:3px 12px;margin:2px;font-size:12px;color:#2ab6b6;">${b.label}</span>`).join(' ')}</div>`
            }
            if (el.type === 'form') {
              return `<div style="font-size:12px;margin:4px 0;">${(el.fields || []).map(f => `<div><strong>${f.label}:</strong> ___</div>`).join('')}</div>`
            }
            return ''
          }).join('')
        }
        return `
          <div style="display:flex;flex-direction:column;margin-bottom:16px;">
            <div style="font-size:11px;color:#888;margin-bottom:4px;${isUser ? 'text-align:right;' : ''}">${label} · ${time}</div>
            <div style="max-width:75%;padding:10px 14px;${bubbleStyle}">${body}</div>
          </div>`
      }).join('')

      const win = window.open('', '_blank')
      win.document.write(`
        <!DOCTYPE html><html><head><title>Chat Export</title>
        <style>body{font-family:sans-serif;font-size:13px;padding:24px;max-width:700px;margin:0 auto;color:#222;}</style>
        </head><body><h3 style="margin-bottom:20px;">Chat Transcript</h3>${rows}</body></html>`)
      win.document.close()
      win.focus()
      win.print()
    },
    async confirmNewChat () {
      this.showNewChatDialog = false
      this.chatReady = false
      this.entryPointOutput = null
      if (this.$refs.chatPanel) {
        await this.$refs.chatPanel.markCompleted()
      }
      this.showChat = false
      await this.$nextTick()
      this.showChat = true
    },
    onOutputSave (payload) {
      console.log('[EntryPoint] save payload', payload)
    },
    onOutputEvent (eventName, payload) {
      const chatId = this.$refs.chatPanel && this.$refs.chatPanel.chatId
      trackEvent({ event_name: eventName, ai_console_chat_id: chatId, properties: payload })
    }
  },
  mounted () {
    trackEvent({ event_name: 'entry_point_console_opened', properties: { project: null, market: null } })
    this._onKeyDown = (e) => { if (e.key === 'Escape') this.showGuide = false }
    document.addEventListener('keydown', this._onKeyDown)
  },
  beforeDestroy () {
    document.removeEventListener('keydown', this._onKeyDown)
  }
}
</script>
<style scoped src="./CreateNewEntryPoint.css"></style>



// WEBPACK FOOTER //
// CreateNewEntryPoint.vue?473db588